/**
 * ENG-7363 - classify and report model-API failures for fleet-wide early
 * warning.
 *
 * The manager sees model-API errors (Anthropic 529 "overloaded", 429
 * rate-limit, 5xx, timeouts/connection errors) in two places:
 *   (a) the spawned Claude Code agent's runtime output (pane.log), scraped by
 *       {@link scrapeAgentRuntimeModelApiError}; and
 *   (b) its own model calls (conversation-eval + memory-extraction backends),
 *       whose thrown errors are classified by {@link classifyModelApiError}.
 *
 * Both funnel through {@link reportModelApiError}, which POSTs a compact,
 * content-free event to the AGT API. Everything here is best-effort: a report
 * failure is logged and swallowed so telemetry never disrupts the agent or the
 * manager.
 *
 * Gating (the `model-api-error-reporting` flag, ships dark) lives at the call
 * sites in the manager, not here - this module is pure classification + a
 * fire-and-forget POST so it stays unit-testable without the flag store.
 */
import { api, getHostId } from './api-client.js';

export type ModelApiErrorClass =
  | 'overloaded_529'
  | 'rate_limit_429'
  | 'server_error_5xx'
  | 'timeout'
  | 'connection_error'
  | 'other';

export type ModelApiErrorSource = 'agent_runtime' | 'conversation_eval' | 'memory_extraction';

export interface ClassifiedModelApiError {
  errorClass: ModelApiErrorClass;
  /** HTTP status when the failure was an HTTP response; null otherwise. */
  httpStatus: number | null;
}

/** Map an HTTP status to a tracked error class, or null if we don't track it. */
export function classifyHttpStatus(status: number): ModelApiErrorClass | null {
  if (status === 529) return 'overloaded_529';
  if (status === 429) return 'rate_limit_429';
  if (status >= 500 && status <= 599) return 'server_error_5xx';
  return null;
}

/**
 * Classify a model-call failure from a thrown error / message / status.
 *
 * The manager's backend runners throw `Error("... returned <status>")`
 * (conversation-evaluator.ts) or an AbortSignal timeout / network error. We
 * classify by (in priority order): an explicit status, a `returned <status>`
 * substring, then timeout / connection / overloaded keywords. Returns null when
 * the input doesn't look like a tracked model-API failure (so we don't report
 * noise).
 */
export function classifyModelApiError(input: {
  error?: unknown;
  status?: number | null;
  message?: string | null;
}): ClassifiedModelApiError | null {
  if (typeof input.status === 'number') {
    const cls = classifyHttpStatus(input.status);
    if (cls) return { errorClass: cls, httpStatus: input.status };
  }

  const err = input.error;
  const rawMessage =
    input.message ??
    (err instanceof Error ? err.message : typeof err === 'string' ? err : '');
  const errName = err instanceof Error ? err.name : '';
  const haystack = `${errName} ${rawMessage}`.toLowerCase();
  if (!haystack.trim()) return null;

  // "... returned 529" / "returned 500" - the runners' exact shape.
  const statusMatch = haystack.match(/returned\s+(\d{3})\b/);
  if (statusMatch) {
    const status = Number(statusMatch[1]);
    const cls = classifyHttpStatus(status);
    if (cls) return { errorClass: cls, httpStatus: status };
  }

  // AbortSignal.timeout throws a TimeoutError / "aborted"; requests can also
  // surface an explicit "timed out".
  if (
    errName === 'TimeoutError' ||
    errName === 'AbortError' ||
    /\btimed?\s*out\b|\btimeout\b|\baborted\b|etimedout/.test(haystack)
  ) {
    return { errorClass: 'timeout', httpStatus: null };
  }

  // Transport-level failures before an HTTP response.
  if (
    /econnreset|econnrefused|enotfound|eai_again|epipe|socket hang up|network error|fetch failed|und_err/.test(
      haystack,
    )
  ) {
    return { errorClass: 'connection_error', httpStatus: null };
  }

  // Anthropic overloaded surfaces as 529, but the word can appear without a
  // status too (subprocess `claude -p` path).
  if (/overloaded/.test(haystack)) {
    return { errorClass: 'overloaded_529', httpStatus: null };
  }

  return null;
}

/**
 * Matches Claude Code's TUI error banner in a pane.log tail:
 *
 *   "API Error: 529 {"type":"error",...}"
 *   "API Error: 429 ..."
 *   "API Error: Overloaded"
 *
 * Anchored on the literal "API Error" prefix Claude Code renders, to avoid
 * false positives from ordinary content that merely contains a 3-digit number.
 */
const AGENT_API_ERROR_RE = /API\s*Error[:\s]+(?:(\d{3})\b|([A-Za-z][^\n]{0,60}))/gi;

/**
 * Scrape the LAST model-API error from an agent pane.log tail. Last-wins
 * because the banner re-renders each frame - the newest reflects current
 * state. Returns null when no tracked error is present ("not detected this
 * tick", not "definitely healthy").
 */
export function scrapeAgentRuntimeModelApiError(paneTail: string): ClassifiedModelApiError | null {
  if (!paneTail) return null;
  let last: ClassifiedModelApiError | null = null;
  let m: RegExpExecArray | null;
  AGENT_API_ERROR_RE.lastIndex = 0;
  while ((m = AGENT_API_ERROR_RE.exec(paneTail)) !== null) {
    if (m[1]) {
      const status = Number(m[1]);
      const cls = classifyHttpStatus(status);
      if (cls) last = { errorClass: cls, httpStatus: status };
      continue;
    }
    const text = (m[2] ?? '').toLowerCase();
    if (/overloaded/.test(text)) last = { errorClass: 'overloaded_529', httpStatus: null };
    else if (/\btimed?\s*out\b|\btimeout\b/.test(text)) last = { errorClass: 'timeout', httpStatus: null };
  }
  return last;
}

/**
 * POST a single model-API error event to the AGT API. Best-effort and
 * non-throwing - a report failure is logged and swallowed.
 *
 * The caller is responsible for the `model-api-error-reporting` flag gate.
 */
export async function reportModelApiError(opts: {
  source: ModelApiErrorSource;
  errorClass: ModelApiErrorClass;
  httpStatus?: number | null;
  modelId?: string | null;
  agentCodeName?: string | null;
  detail?: string | null;
  occurredAt?: string;
  log?: (msg: string) => void;
}): Promise<void> {
  try {
    const hostId = await getHostId();
    if (!hostId) return;
    await api.post('/host/model-api-error-events', {
      host_id: hostId,
      agent_code_name: opts.agentCodeName ?? null,
      source: opts.source,
      error_class: opts.errorClass,
      http_status: opts.httpStatus ?? null,
      model_id: opts.modelId ?? null,
      // Content-free by contract, but bound it defensively.
      detail: opts.detail ? opts.detail.slice(0, 500) : null,
      occurred_at: opts.occurredAt ?? new Date().toISOString(),
    });
  } catch (err) {
    opts.log?.(
      `[model-api-error] report failed (${opts.source}/${opts.errorClass}): ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}
